import { FilterOptions } from '@app/components/data-table/hooks/use-server-filter-options';
import { PickerPeriod } from '@app/components/multi-period-picker';
import { PackagingOperationActivityRecord } from '@shared/api/types';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import { ColumnDef } from '@tanstack/react-table';

export function useSoldPackagingItemSalesTableColumns({
  filters,
  timePeriods,
}: {
  filters: FilterOptions<['timePeriods', 'sites', 'companyReports']>;
  timePeriods: PickerPeriod[];
}): ColumnDef<PackagingOperationActivityRecord>[] {
  return [
    {
      id: 'uuid',
      accessorKey: 'uuid',
      enableHiding: true,
    },
    {
      id: 'companyReport',
      accessorKey: 'companyReport',
      enableHiding: true,
      enableColumnFilter: true,
      meta: {
        label: 'Company report',
        variant: 'multiSelect',
        options: filters.companyReports,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },

    {
      id: 'period',
      accessorKey: 'timePeriod',
      header: ({ column }) => <HeaderCell column={column} name="Period" />,
      cell: ({ getValue }) => {
        const period =
          getValue<PackagingOperationActivityRecord['timePeriod']>();
        return <TextCell text={period?.name} />;
      },
      meta: {
        label: 'Time period',
        variant: 'periodPicker',
        periods: timePeriods,
        sorting: { ascLabel: 'Oldest', descLabel: 'Newest' },
      },

      enableColumnFilter: true,
    },
    {
      id: 'site',
      accessorKey: 'site',
      enableColumnFilter: true,
      header: ({ column }) => <HeaderCell column={column} name="Site" />,
      cell: ({ getValue }) => {
        const site = getValue<PackagingOperationActivityRecord['site']>();
        return <TextCell text={site?.name} />;
      },
      meta: {
        label: 'Site',
        variant: 'multiSelect',
        options: filters.sites,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'customer',
      accessorKey: 'customer',
      enableSorting: false,
      header: ({ column }) => <HeaderCell column={column} name="Customer" />,
      cell: ({ getValue }) => {
        const customer =
          getValue<PackagingOperationActivityRecord['customer']>();
        return <TextCell text={customer?.name} />;
      },
    },

    {
      id: 'quantity',
      accessorKey: 'quantity',
      header: ({ column }) => (
        <HeaderCell column={column} name="Packs sold" numberColumn />
      ),
      cell: ({ getValue }) => {
        const quantity = getValue<number>();
        return <NumberCell number={quantity} />;
      },
    },

    {
      id: 'totalQuantity',
      accessorKey: 'totalQuantity',
      header: ({ column }) => (
        <HeaderCell column={column} name="Total quantity" numberColumn />
      ),
      cell: ({ getValue }) => {
        const totalQuantity = getValue<number>();
        return <NumberCell number={totalQuantity} />;
      },
    },
  ];
}
